import { setIsLoading } from '@redux/actions';
import { setCplAccountSettings, setMagentoSettings } from '@redux/actions/accountSettingsActions';
import { setLoaded } from '@redux/actions/alreadyLoadedActions';
import { getCompanyPendingOrFirstCompanyEntitlements, getEntitlements } from '@redux/actions/entilementsActions';
import { getProfileDetails } from '@redux/actions/profileActions';
import axios from 'axios';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { CredibilityEntitlement, IState, VboEntitlement } from '@customTypes/ItemTypes';
import { ALL_API_ENHANCEMENT, PRODUCT_TITLES_CI_UKI, VBO_PRODUCTS } from '@helpers/constants';
import { setFreemiumProdDaysLeft } from '@redux/actions/freemiumProdDaysLeftActions';
import { isCountryCodeUKI } from '@helpers/isEuropeRegion';

export function useEntitlements() {
    const dispatch = useDispatch();
    const [hideContent, setHideContent] = useState(true);
    const profileDetails = useSelector((state: IState) => state.profileDetails);

    const hasloaded = useSelector((state: IState) => state.alreadyLoaded);
    useEffect(() => {
        if (!hasloaded) {
            const getUserProfiledata = async () => {
                setIsLoading(true);
                await dispatch(getProfileDetails())
                    .finally(() => setIsLoading(false));
            };
            const getEntitlementsData = async () => {
                setIsLoading(true);
                setHideContent(true);
                await dispatch(ALL_API_ENHANCEMENT ? getCompanyPendingOrFirstCompanyEntitlements() : getEntitlements())
                    // @ts-ignore
                    .then((resp) => {
                        const isUKUser = isCountryCodeUKI(profileDetails.countryCode);
                        const isCreditInsightFromMagento = isUKUser && resp?.entitlements.credibilityEntitlements.filter((product: CredibilityEntitlement) => product.title === PRODUCT_TITLES_CI_UKI.CREDIT_INSIGHTS_BASIC || PRODUCT_TITLES_CI_UKI.CREDIT_INSIGHTS_FREE).length > 0;

                        const isVBOProductsAvailable = resp?.entitlements?.vboEntitlements?.filter(
                            (product: VboEntitlement) => /D&B Rev\.Up.*/g.test(product.product_name)
                                || VBO_PRODUCTS.includes(product.product_name) || ['329', '330', '331'].includes(product.application_id)
                        ).length > 0;

                        const isMagento = isVBOProductsAvailable || isCreditInsightFromMagento;

                        const cpl = resp?.entitlements?.vboEntitlements?.filter(
                            (product: VboEntitlement) => product.product_name === 'Analytics STD Trial'
                        );
                        /* Account settings link display based on  priority  https://jira.aws.dnb.com/browse/CPL-1449
                           atleast one Magento(Magento account settings) > SMB(EB Dashboard account settings-if no Magento) > ST trail(CP account settings-if no Magento).
                           Also, CM for UK user (https://dnbenterprise.atlassian.net/browse/ME-1336) */
                        if (isMagento) {
                            dispatch(setMagentoSettings(isMagento));
                        }
                        else if (cpl.length > 0) {
                            dispatch(setCplAccountSettings(cpl.length !== 0));
                        }
                        setHideContent(false);
                    })
                    .finally(() => {
                        setIsLoading(false);
                    });
            };

            getUserProfiledata();
            getEntitlementsData();
            //  setHideContent(true);
            dispatch(setLoaded());
        }
        else {
            setIsLoading(false);
            setHideContent(false);
        }
    }, []);

    return [hideContent];
}

const productSubscriptions = ['216', '231', '253', '294'];
// 253 : 'D&B Rev.Up Now',//ZETA
// 231 : 'D&B Rev.Up ABX for Web Essential',//SSVI
// 216 : 'Analytics STD Trial'
// 294  : 'CIUKIFree //Credit Insights'
export const subScriptionProductAvailable = async (product_subscriptions: any, dispatch: any) => {
    try {
        const allProductSubscriptionData = await Promise.all(
            product_subscriptions.map(async (product: any) => {
                const response = await axios.get(
                    `/services/auth/accessSubscriptions?subscriber_number=${product.subscriber_number}&app_id=${product.app_id}`
                );

                const isExpirationDateAvailable = response?.data[0]?.products[0];
                const userDetail = response?.data?.userDetails.filter((prodInfo: any) => product.app_id === prodInfo.app_id)[0];
                if (isExpirationDateAvailable) {
                    const { expiration_date, service_type } = isExpirationDateAvailable;
                    // const userDetailProduct = response.data.userDetails.filter((el: any) => el.app_id === product.app_id);
                    // const userDetailCheck = userDetailProduct.length === 0 || (userDetailProduct[0].products !== undefined && userDetailProduct[0].products[0].packages.length === 1 && (userDetailProduct[0].products[0].packages[0].package_name.includes('Trial')));
                    const isFreemium = isExpirationDateAvailable?.name === 'Analytics STD Trial'
                        ? true : isExpirationDateAvailable?.service_type === 'Free Trial';
                    const daysLeft = productSubscriptions.includes(product.app_id) && isFreemium ? getSubScriptionDays(expiration_date) : -1;
                    const { subscriber_number, app_id, product_name } = product;
                    const subscription_status = userDetail?.products[0]?.subscription_status;
                    return {
                        subscriber_number,
                        app_id,
                        product_name,
                        expiration_date,
                        service_type,
                        daysLeft,
                        subscription_status
                    };
                }
                if (!response?.data[0]?.products.length) {
                    return {
                        subscriber_number: userDetail?.products[0]?.subscriber_number,
                        app_id: userDetail?.app_id,
                        product_name: userDetail.app_name,
                        expiration_date: null,
                        service_type: null,
                        daysLeft: -1,
                        subscription_status: userDetail?.products[0]?.subscription_status
                    };
                }
                return {
                    subscriber_number: null,
                    app_id: null,
                    product_name: null,
                    expiration_date: null,
                    service_type: null,
                    daysLeft: null,
                    subscription_status: null
                };
            })
        );
        dispatch(setFreemiumProdDaysLeft([...allProductSubscriptionData]));
    }
    catch (error) {
        console.log('error in subScriptionProductAvailable method');
    }
};

function getSubScriptionDays(expiration_date: string): number {
    try {
        if (expiration_date) {
            const expiryDate = new Date(expiration_date);
            const today = new Date(Date.now());
            // @ts-ignore
            const diff = Math.ceil((expiryDate - today) / (1000 * 3600 * 24));
            return diff;
        }
        return -1;
    }
    catch (err: any) {
        console.log('error in accessSubscription method', err.message);
        return -1;
    }
}
